import { FormErrorMessage, FormHelperText } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormControl,
  Input,
  SignupControl,
} from '@time-line/components/common';
import {
  NEWSLETTER_LIST_ID_SKINCARE,
  NEWSLETTER_LIST_ID_SUPPLEMENTS,
} from 'components/common/NewsletterSignup/constants';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { useListSignup } from 'hooks/useListSignup';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const validationSchema = z.object({
  email: z.email({ error: 'Email is invalid' }),
});

type FormSchema = z.infer<typeof validationSchema>;

export enum Variant {
  INLINE = 'inline',
  STACKED = 'stacked',
}

export type NewsletterSignupProps = {
  size?: 'md' | 'lg';
  variant?: Variant;
  source: string;
  colorScheme?: 'white' | 'gray';
};

export function NewsletterSignup({
  size,
  variant = Variant.INLINE,
  source,
  colorScheme = 'gray',
}: NewsletterSignupProps) {
  const { mutateAsync, isPending } = useListSignup();
  const isSkincare = useIsSkincare();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { isReady, errors, isSubmitSuccessful },
  } = useForm<FormSchema>({
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async ({ email }: { email: string }) => {
    try {
      await mutateAsync({
        email,
        source: `timeline_website/${source}`,
        listId: isSkincare
          ? NEWSLETTER_LIST_ID_SKINCARE
          : NEWSLETTER_LIST_ID_SUPPLEMENTS,
      });
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        setError('email', {
          type: 'manual',
          message: err.message,
        });
      }
    }
  };

  const isSuccess = isSubmitSuccessful && !errors.email;

  useEffect(() => {
    if (!isReady) return;
    const handleReset = () => reset();
    router.events.on('routeChangeComplete', handleReset);
    return () => router.events.off('routeChangeComplete', handleReset);
  }, [router.events, isReady, reset]);

  return (
    <form className="newsletter-signup" onSubmit={handleSubmit(onSubmit)}>
      {variant === Variant.INLINE && (
        <SignupControl
          id={`${source}-email`}
          size={size}
          error={errors.email}
          isSuccess={isSuccess}
          inputProps={{ ...register('email') }}
          isLoading={isPending}
        />
      )}

      {variant === Variant.STACKED && (
        <FormControl isInvalid={!!errors.email}>
          <>
            <Input
              size={size}
              type="email"
              autoComplete="email"
              placeholder="Email"
              colorScheme={colorScheme}
              {...register('email')}
            />

            <FormErrorMessage fontSize="2xs" color="red.300">
              {errors.email?.message}
            </FormErrorMessage>

            {isSuccess && (
              <FormHelperText fontSize="2xs" color="green.600">
                Success!
              </FormHelperText>
            )}

            <Button
              type="submit"
              isLoading={isPending}
              width="100%"
              mt="2xs"
              colorScheme={colorScheme}
            >
              Sign up
            </Button>
          </>
        </FormControl>
      )}
    </form>
  );
}
